import React, { useEffect, useRef } from 'react';
import { usePlayerContext } from '../../context/player-context';
import { isSignificantMouseMovement } from '../../utils/event-utils';

interface VideoContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const VideoContainer: React.FC<VideoContainerProps> = ({ children, className = '' }) => {
  const {
    playerRef,
    showPlayerControls,
    hidePlayerControls,
    handleUserInteractionStart,
    handleUserInteractionEnd,
    handleContainerClick,
    handleVideoContainerTap,
    hidePreview,
    aspectRatio,
    isVerticalVideo,
    handleAspectRatio,
  } = usePlayerContext();

  // Track if we've set up container classes
  const isSetupRef = useRef(false);

  // Set up the container
  useEffect(() => {
    if (playerRef.current && !isSetupRef.current) {
      playerRef.current.classList.add('player-container');
      isSetupRef.current = true;
    }
  }, [playerRef]);

  // Handle mouse/touch events
  useEffect(() => {
    const container = playerRef.current;
    if (!container) return;

    let lastMoveTime = 0;
    const moveThreshold = 50; // ms

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      // Debounce movement events
      if (now - lastMoveTime <= moveThreshold) {
        return;
      }
      lastMoveTime = now;

      // Only respond to real mouse movement
      if (!isSignificantMouseMovement(e)) {
        return;
      }

      // Show controls
      showPlayerControls();

      // Check if mouse is over the progress bar
      const progressBar = document.getElementById('progress-bar-container');
      if (progressBar) {
        const progressRect = progressBar.getBoundingClientRect();
        const isOverProgressBar =
          e.clientX >= progressRect.left &&
          e.clientX <= progressRect.right &&
          e.clientY >= progressRect.top &&
          e.clientY <= progressRect.bottom;

        // Hide preview if not over progress bar
        if (!isOverProgressBar) {
          hidePreview();
        }
      }
    };

    const handleMouseEnter = () => {
      showPlayerControls();
    };

    const handleMouseLeave = () => {
      hidePlayerControls();
      hidePreview();
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [playerRef, showPlayerControls, hidePlayerControls, hidePreview]);

  // Use the original container markup when handleAspectRatio is false (default behavior)
  if (!handleAspectRatio) {
    return (
      <div
        ref={playerRef as unknown as React.RefObject<HTMLDivElement>}
        className={`relative w-full overflow-hidden bg-black cursor-pointer aspect-video rounded-xl player-container ${className}`}
        style={{ position: 'relative' }}
        onClick={(e) => {
          if (handleVideoContainerTap) {
            handleVideoContainerTap();
          } else {
            handleContainerClick();
          }
        }}
        onMouseDown={() => {
          handleUserInteractionStart();
        }}
        onMouseUp={() => {
          setTimeout(() => {
            handleUserInteractionEnd();
          }, 500);
        }}
        onTouchStart={() => {
          handleUserInteractionStart();
        }}
        onTouchEnd={() => {
          setTimeout(() => {
            handleUserInteractionEnd();
          }, 500);
        }}
      >
        {children}
      </div>
    );
  }

  // For handleAspectRatio=true, use the new vertical video aware container
  return (
    <div
      ref={playerRef as unknown as React.RefObject<HTMLDivElement>}
      className={`relative bg-black cursor-pointer rounded-xl player-container ${
        isVerticalVideo
          ? 'aspect-[9/16] h-full max-w-[500px] mx-auto'
          : 'aspect-video w-full h-full'
      } ${className}`}
      style={{
        position: 'relative',
        minHeight: '300px',
        display: 'block',
      }}
      onClick={(e) => {
        if (handleVideoContainerTap) {
          handleVideoContainerTap();
        } else {
          handleContainerClick();
        }
      }}
      onMouseDown={handleUserInteractionStart}
      onMouseUp={() => {
        setTimeout(() => {
          handleUserInteractionEnd();
        }, 500);
      }}
      onTouchStart={handleUserInteractionStart}
      onTouchEnd={() => {
        setTimeout(() => {
          handleUserInteractionEnd();
        }, 500);
      }}
    >
      {children}
    </div>
  );
};
